import { useEffect, useRef, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { ArrowIcon, CheckCircleIcon } from "../../asset/icons";
import NGNFlagImg from "asset/images/Currency.svg";
import GBPFlagImg from "asset/images/GB.svg";
import USFlagImg from "asset/images/US.png";
import EUFlagImg from "asset/images/EU.svg";
import USDCIcon from "asset/icons/USDC.svg";
import USDTIcon from "asset/icons/USDT.svg";
import GenericCurrencyIcon from "asset/images/Currency.svg";

import { fetchSupportedCurrencies } from "api/fetchers";
import { formatMoney } from "utilities/formatMoney";
import { cn } from "lib/utils";
import currencyPairCheck from "utilities/currencyPairCheck";

export const CurrencySelector = ({
    wallets = [],
    selected,
    onSelect,
    businessId: propBusinessId,
    showBalances = false,
    exclude = [],
    currencies,
    excludeProviderStatuses = [],
    currencyFrom,
}) => {
    const EMPTY = useMemo(() => [], []);
    const upper = (c) => String(c || "").toUpperCase();

    const excludeSet = useMemo(
        () => new Set((Array.isArray(exclude) ? exclude : [exclude]).filter(Boolean).map(upper)),
        [exclude]
    );

    const [modalOpen, setModalOpen] = useState(false);
    const [supported, setSupported] = useState([]);
    const [loading, setLoading] = useState(false);
    const [menuStyle, setMenuStyle] = useState(null);

    const wrapperRef = useRef(null);
    const menuRef = useRef(null);
    const reduxBusinessId = useSelector((s) => s?.business?._id);
    const businessId = propBusinessId || reduxBusinessId;

    const externalList = Array.isArray(currencies) ? currencies : EMPTY;
    const hasExternalCurrencies = externalList.length > 0;

    useEffect(() => {
        if (hasExternalCurrencies) {
            setLoading(false);
            return;
        }

        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const list = await fetchSupportedCurrencies({
                    normalize: true,
                    businessId,
                    currencyFrom,
                });
                if (!cancelled) setSupported(Array.isArray(list) ? list : []);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [businessId, currencyFrom, hasExternalCurrencies]);

    const getProviderStatus = (w) => {
        if (!w || !Array.isArray(w.providers)) return "";
        const provider = w.providers.find((p) => p?.accountNumber) || w.providers[0] || null;
        return String(provider?.status || w.providerStatus || "").toLowerCase();
    };

    const defaultNameByCode = (code, fallback = code) => {
        const map = {
            NGN: "Naira",
            USD: "Dollar",
            EUR: "Euro",
            GBP: "Pound",
            GHS: "Cedi",
            KES: "KES",
            UGX: "UGX",
            CAD: "Canadian Dollar",
            USDC: "USDC",
            USDT: "USDT",
        };
        return map[code] || fallback;
    };
    const defaultSymbolByCode = (code, fallback = "") => {
        const map = {
            NGN: "₦",
            USD: "$",
            EUR: "€",
            GBP: "£",
            GHS: "₵",
            KES: "KSh",
            UGX: "USh",
            CAD: "$",
            USDC: "USDC",
            USDT: "USDT",
        };
        return map[code] || fallback;
    };
    const flagByCode = (code) => {
        switch (code) {
            case "NGN":
                return NGNFlagImg;
            case "GBP":
                return GBPFlagImg;
            case "USD":
                return USFlagImg;
            case "EUR":
                return EUFlagImg;
            case "USDC":
                return USDCIcon;
            case "USDT":
                return USDTIcon;
            default:
                return GenericCurrencyIcon;
        }
    };

    const walletMap = useMemo(() => {
        return wallets.reduce((acc, w) => {
            if (!w?.currency) return acc;
            acc[upper(w.currency)] = w;
            return acc;
        }, {});
    }, [wallets]);

    const excludedStatusSet = useMemo(
        () =>
            new Set(
                (excludeProviderStatuses || []).filter(Boolean).map((s) => String(s).toLowerCase())
            ),
        [excludeProviderStatuses]
    );

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && wrapperRef.current.contains(e.target)) return;
            if (menuRef.current && menuRef.current.contains(e.target)) return;
            setModalOpen(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        if (!modalOpen) return;

        const computeMenuStyle = () => {
            const rect = wrapperRef.current?.getBoundingClientRect();
            if (!rect) return;

            const padding = 8;
            const desiredWidth = 340;
            const maxWidth = Math.min(desiredWidth, window.innerWidth - padding * 2);
            const left = Math.max(
                padding,
                Math.min(rect.right - maxWidth, window.innerWidth - padding - maxWidth)
            );

            let top = rect.bottom + 8;
            let maxHeight = Math.min(320, window.innerHeight - top - padding);

            if (maxHeight < 160) {
                const spaceAbove = rect.top - padding - 8;
                if (spaceAbove > maxHeight) {
                    maxHeight = Math.min(320, spaceAbove);
                    top = Math.max(padding, rect.top - 8 - maxHeight);
                }
            }

            setMenuStyle({
                position: "fixed",
                top,
                left,
                width: maxWidth,
                maxHeight,
                zIndex: 1000,
            });
        };

        computeMenuStyle();
        window.addEventListener("resize", computeMenuStyle);
        window.addEventListener("scroll", computeMenuStyle, true);

        return () => {
            window.removeEventListener("resize", computeMenuStyle);
            window.removeEventListener("scroll", computeMenuStyle, true);
        };
    }, [modalOpen]);

    const sourceList = (hasExternalCurrencies ? externalList : supported).map((r) => ({
        code: upper(r.code || r.symbol),
        name: r.name,
        shortName: r.shortName,
        symbol: r.symbol,
        quote: r.quote,
        hasProvider: r.hasProvider,
        providerOnRamp: r.providerOnRamp,
        providerOffRamp: r.providerOffRamp,
    }));

    const rows = useMemo(() => {
        const base = sourceList.filter((r) => r.code && !excludeSet.has(r.code));

        const seen = new Set();
        const out = [];
        for (const r of base) {
            if (seen.has(r.code)) continue;
            seen.add(r.code);

            const w = walletMap[r.code];
            if (w) {
                const st = getProviderStatus(w);
                if (excludedStatusSet.has(st)) continue;
            }

            out.push({
                ...r,
                name: r.name || defaultNameByCode(r.code),
                shortName: r.shortName || r.name || defaultNameByCode(r.code),
                symbol: r.symbol || defaultSymbolByCode(r.code),
            });
        }
        return out;
    }, [sourceList, walletMap, excludeSet, excludedStatusSet, getProviderStatus]);

    // figure out selected + meta
    const selectedU = upper(selected);
    const activeCode =
        selectedU && !excludeSet.has(selectedU) && rows.some((r) => r.code === selectedU)
            ? selectedU
            : rows[0]?.code || "";
    const activeMeta = rows.find((r) => r.code === activeCode) || null;

    useEffect(() => {
        if (activeCode && selectedU !== activeCode) {
            onSelect && onSelect(activeCode, activeMeta);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeCode]);

    const imgUrl = (code) =>
        code
            ? `https://trade-vu.lon1.digitaloceanspaces.com/trade-vu/${String(
                  code
              ).toLowerCase()}.png`
            : "";

    const menu =
        modalOpen && menuStyle ? (
            <div
                ref={menuRef}
                className="bg-white space-y-2 py-3 px-2 overflow-auto rounded-lg shadow"
                style={menuStyle}
                role="listbox"
            >
                {loading && (
                    <div className="px-2 py-3 text-sm text-gray-500">Loading currencies…</div>
                )}

                {!loading &&
                    rows.map((row) => {
                        const code = row.code;
                        const isSelected = code === activeCode;
                        const wallet = walletMap[code];
                        const balance = wallet?.balance;

                        return (
                            <div
                                key={code}
                                role="option"
                                aria-selected={isSelected}
                                onClick={() => {
                                    onSelect && onSelect(code, row);
                                    setModalOpen(false);
                                }}
                                className={cn(
                                    "flex items-center justify-between gap-2 p-2 rounded-lg cursor-pointer transition",
                                    isSelected
                                        ? "border-1 border-solid !border-[#6F00FF]"
                                        : "border border-transparent"
                                )}
                            >
                                <div className="flex gap-2 items-center">
                                    <img
                                        src={imgUrl(code)}
                                        alt=""
                                        className="w-10 h-10 rounded-full object-cover"
                                    />

                                    <div className="min-w-0">
                                        <p
                                            className="font-semibold break-words leading-snug"
                                            title={row.name || row.shortName || code}
                                        >
                                            {row.name || row.shortName}
                                        </p>
                                        {showBalances ? (
                                            <span className="text-[#667085]">
                                                {currencyPairCheck(code)}{" "}
                                                {typeof balance === "number"
                                                    ? formatMoney(balance.toFixed(2))
                                                    : "—"}
                                            </span>
                                        ) : (
                                            <span className="text-[#667085] text-sm">
                                                {row.shortName || code}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {isSelected ? (
                                    <CheckCircleIcon className="size-5" />
                                ) : (
                                    <CheckCircleIcon className="size-5 *:fill-[#DBDFE5]" />
                                )}
                            </div>
                        );
                    })}

                {!loading && rows.length === 0 && (
                    <div className="px-3 py-4 text-sm text-gray-500" role="status">
                        No recipient currencies are available
                        {currencyFrom ? ` when sending from ${upper(currencyFrom)}` : ""} right now.
                    </div>
                )}
            </div>
        ) : null;

    return (
        <div className="relative min-w-[100px]" ref={wrapperRef}>
            <button
                className="flex items-center gap-1 text-sm bg-transparent px-2 py-1"
                onClick={() => setModalOpen((p) => !p)}
                aria-haspopup="listbox"
                aria-expanded={modalOpen}
            >
                {activeCode || "Select"}
                <ArrowIcon className="w-4 h-4 ml-1" />
            </button>

            {typeof document !== "undefined" ? createPortal(menu, document.body) : menu}
        </div>
    );
};
